// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { sectionTitleStyles, SectionTitleStyles } from "@mcmc/App/Widgets/SectionTitleWidget/SectionTitleWidget";
import { styledBox } from "@mcmc/XDL/Components/Common/Box";
import { css } from "styled-components";

export interface OrderInformationSectionGlobalStyles {
    itemWrapper: InjectableCss;
    itemTitleText: TypographyProps;
    itemGrid?: GridItemProps;
}

export const orderInformationSectionGlobalStyles: OrderInformationSectionGlobalStyles = {
    itemWrapper: {
        css: css`
            flex-direction: column;
        `,
    },
    itemTitleText: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 18px;
            }
            margin-bottom: 5px;
        `,
    },
    itemGrid: {
        width: [6, 3, 3, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
};

export interface TotalSectionGlobalStyles {
    breadcrumbActionsWrapper: InjectableCss;
    container?: GridContainerProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    divider?: InjectableCss;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
}

export const totalSectionGlobalStyles: TotalSectionGlobalStyles = {
    breadcrumbActionsWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            & > * {
                margin-left: 25px;
                &:first-child {
                    margin: 0;
                }
            }
        `,
    },
    container: {
        gap: 8,
        css: css`
            ${styledBox};
            padding: 15px 22px;
            margin-bottom: 30px;
        `,
    },
    totalLabel: {
        weight: "bold",
        css: css`
            margin-top: 7px;
        `,
    },
    totalValue: {
        weight: "bold",
        css: css`
            margin-top: 7px;
            display: block;
        `,
    },
    divider: {
        css: css`
            width: calc(100% + 44px);
            margin: 0 -22px;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    labelGridItem: { width: 5 },
    valueGridItem: {
        width: 7,
        css: css`
            text-align: right;
            display: inline-block;
            ${TypographyStyle} {
                word-break: initial;
                overflow-wrap: initial;
            }
        `,
    },
};

export interface AddressesSectionGlobalStyles {
    AddressGridItem?: GridItemProps;
    addressesTitle?: SectionTitleStyles;
}

export const addressesSectionGlobalStyles: AddressesSectionGlobalStyles = {
    AddressGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            flex-direction: column;
            align-items: stretch;
        `,
    },
    addressesTitle: {
        wrapper: {
            css: css`
                ${sectionTitleStyles.wrapper?.css}

                @media print {
                    font-size: 15px;
                }

                margin-bottom: 0;
            `,
        },

        title: {
            css: css`
                ${sectionTitleStyles.title?.css}

                padding: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,

                        css`
                            font-size: 20px;
                        `,

                        css`
                            font-size: 22px;
                        `,

                        null,

                        null,
                    ])}
            `,
        },
    },
};
